import { ReactNode } from 'react';
import AntdMessage from 'antd/lib/message';

/**
 * {@docCategory Message}
 */
export interface MessageOptions {
   /**
    * The content to display.
    */
   content: ReactNode;

   /**
    * The type of the message.
    * @defaultvalue 'info'
    */
   type?: 'error' | 'info' | 'success' | 'warning' | 'loading';

   /**
    * The unique message id. You need to use this if you set the duration to 0 and need to dismiss
    * the message under application control.
    */
   messageId?: string | number;

   /**
    * Time in seconds before the message auto-dismisses, don't dismiss if set to 0.
    * @defaultvalue 3
    */
   duration?: number;

   /**
    * Callback called when the message is closed. This will not be called if the application
    * dismisses the message using the close() or closeAll functions.
    */
   onClose?: () => void;
}

export interface MessageType extends PromiseLike<any> {
   (): void;
}

export class Message {
   static error(content: ReactNode, messageId?: string | number): MessageType {
      return AntdMessage.error({
                                  content: content,
                                  key: messageId,
                                  duration: 1.5
                               });
   }

   static info(content: ReactNode, messageId?: string | number): MessageType {
      return AntdMessage.info({
                                 content: content,
                                 key: messageId,
                                 duration: 1.5
                              });
   }

   static success(content: ReactNode, messageId?: string | number): MessageType {
      return AntdMessage.success({
                                    content: content,
                                    key: messageId,
                                    duration: 1.5
                                 });
   }

   static warning(content: ReactNode, messageId?: string | number): MessageType {
      return AntdMessage.warning({
                                    content: content,
                                    key: messageId,
                                    duration: 1.5
                                 });
   }

   static loading(content: ReactNode,
                  messageId?: string | number,
                  durationInSeconds?: number): MessageType {
      return AntdMessage.loading({
                                    content: content,
                                    key: messageId,
                                    duration: durationInSeconds === undefined ? 3 : durationInSeconds
                                 });
   }

   static show(options: MessageOptions): MessageType {
      return AntdMessage.open({
                                 type: options.type === undefined ? 'info' : options.type,
                                 content: options.content,
                                 key: options.messageId,
                                 duration: options.duration === undefined ? 1.5 : options.duration,
                                 onClose: options.onClose
                              });
   }

   static close(messageId: string | number) {
      AntdMessage.destroy(messageId);
   }

   static closeAll() {
      AntdMessage.destroy();
   }
}
